import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function Card({ children, testId }) {
  return <div className="p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid={testId}>{children}</div>;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [freshness, setFreshness] = useState([]);
  const [manualPending, setManualPending] = useState([]);
  const [pipelineApprovals, setPipelineApprovals] = useState([]);
  const [inboxCount, setInboxCount] = useState(0);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    http.get("/admin/freshness").then((r) => {
      setFreshness(r.data.items || []);
      setManualPending((r.data.items || []).filter((i) => i.review_state === "PENDING_REVIEW"));
    }).catch(() => {});
    http.get("/admin/pipeline/approvals").then((r) => {
      // Only count tasks whose brand still exists.
      setPipelineApprovals((r.data.tasks || []).filter((t) => t.brand));
    }).catch(() => {});
    http.get("/admin/suggestions", { params: { status: "NEW" } })
      .then((r) => setInboxCount((r.data.suggestions || []).length))
      .catch(() => {});
    http.get("/admin/reports").then((r) => setReports((r.data.reports || []).filter((x) => x.status !== "RESOLVED"))).catch(() => {});
  }, []);

  const stale = freshness.filter((i) => i.stale);

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <h1 className="font-serif text-4xl mb-2">Dashboard</h1>
        <p className="text-sm" style={{ color: "var(--bp-text-muted)" }}>Signed in as {user?.name}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <Card testId="card-freshness-stale">
            <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Freshness — stale (&gt; 90 days)</div>
            <div className="font-serif text-4xl">{stale.length}</div>
            <ul className="mt-4 space-y-1 text-sm">
              {stale.slice(0, 5).map((i) => (
                <li key={i.brand_id} className="flex justify-between">
                  <Link to={`/admin/brands/${i.slug}`} className="bp-link" data-testid={`stale-link-${i.slug}`}>{i.display_name}</Link>
                  <span className="font-mono text-xs" style={{ color: "var(--bp-text-muted)" }}>{i.age_days}d</span>
                </li>
              ))}
              {stale.length === 0 && <li className="text-sm" style={{ color: "var(--bp-text-muted)" }}>No stale profiles.</li>}
            </ul>
          </Card>

          <Card testId="card-review-queues">
            <div className="uppercase text-xs tracking-widest mb-4" style={{ color: "var(--bp-text-muted)" }}>Review queues</div>
            <div className="space-y-4">
              <Link
                to="/admin/approvals"
                className="flex items-baseline justify-between hover:bg-neutral-50 -mx-2 px-2 py-1 rounded"
                data-testid="row-pipeline-approvals"
              >
                <div>
                  <div className="text-sm">Pipeline first-build approvals</div>
                  <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>Automated builds awaiting editor sign-off</div>
                </div>
                <div className="font-serif text-3xl" data-testid="count-pipeline-approvals">{pipelineApprovals.length}</div>
              </Link>
              <Link
                to="/admin/brands?state=PENDING_REVIEW"
                className="flex items-baseline justify-between hover:bg-neutral-50 -mx-2 px-2 py-1 rounded"
                data-testid="row-manual-reviews"
              >
                <div>
                  <div className="text-sm">Manual editor reviews</div>
                  <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>Human-authored drafts awaiting 4-eyes approval</div>
                </div>
                <div className="font-serif text-3xl" data-testid="count-manual-reviews">{manualPending.length}</div>
              </Link>
              <Link
                to="/admin/inbox"
                className="flex items-baseline justify-between hover:bg-neutral-50 -mx-2 px-2 py-1 rounded"
                data-testid="row-inbox"
              >
                <div>
                  <div className="text-sm">Inbox — link submissions</div>
                  <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>New URLs suggested by brands / public</div>
                </div>
                <div className="font-serif text-3xl" data-testid="count-inbox">{inboxCount}</div>
              </Link>
            </div>
          </Card>

          <Card testId="card-open-reports">
            <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Open error reports</div>
            <div className="font-serif text-4xl">{reports.length}</div>
            <ul className="mt-4 space-y-1 text-sm">
              {reports.slice(0, 5).map((r) => (
                <li key={r.id} className="flex justify-between">
                  <Link to="/admin/reports" className="bp-link truncate mr-2">{r.brand_slug || "(no brand)"}</Link>
                  <span className={`font-mono text-xs ${r.sla_days_remaining < 3 ? "text-red-700" : ""}`}>SLA {r.sla_days_remaining ?? "—"}d</span>
                </li>
              ))}
              {reports.length === 0 && <li className="text-sm" style={{ color: "var(--bp-text-muted)" }}>Inbox clear.</li>}
            </ul>
          </Card>
        </div>

        <h2 className="font-serif text-2xl mt-12 mb-4">Freshness queue</h2>
        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Brand</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Sector</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Last reviewed</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Age</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {freshness.map((i) => (
                <tr key={i.brand_id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`freshness-row-${i.slug}`}>
                  <td className="px-4 py-3 font-serif text-base">{i.display_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{i.sector_code}</td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--bp-text-muted)" }}>{i.last_reviewed_at?.slice(0, 10)}</td>
                  <td className={`px-4 py-3 font-mono text-xs ${i.stale ? "text-amber-700" : ""}`}>{i.age_days}d{i.stale && " · stale"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{i.review_state}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/admin/brands/${i.slug}`} className="bp-link text-sm">Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
